import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiString as API_URL, YES } from '../utils/constants';
import { AppThunk } from './store';
import { saveEngagementDraft } from './documentEngagement.slice';
import { copyObject } from '../utils/common';
import { SummaryNoteTypeEnum } from '../Enum/SummaryNoteTypeEnum';
import { gotoNextMenu, setAssessmentError } from './assessment.slice';
import { AbuseHistoryModel } from '../Models/AbuseHistory/AbuseHistoryModel.model';
import { preparePriorAssessmentForNewDraft } from '../utils/aiFlagHelpers';
import { AbuseHistoryItem } from '../Models/AbuseHistory/AbuseHistoryItem.model';

export const PS_ABUSE_ADULT = 'Physical abuse as an adult';
export const DOMESTIC_VIOLENCE = 'Domestic violence/abuse';
export const ELDER_ABUSE = 'Elder abuse';
export const SEXUAL_ABUSE = 'Sexual abuse/molestation';

const abuseHistorySlice = createSlice({
    name: 'abuseHistorySlice',
    initialState: { abuseHistoryInfo: new AbuseHistoryModel(), abuseHistoryOptions: [] as AbuseHistoryItem[], isFetchingAbuseHistory: false },
    reducers: {
        _setIsFetchingAbuseHistory(state, action) {
            state.isFetchingAbuseHistory = action.payload;
        },
        _setAbuseHistoryInfo(state, action) {
            state.abuseHistoryInfo = action.payload;
        },
        _setAbuseHistoryOptions(state, action) {
            state.abuseHistoryOptions = action.payload;
        },
    },
});

const { _setIsFetchingAbuseHistory, _setAbuseHistoryInfo, _setAbuseHistoryOptions } = abuseHistorySlice.actions;

export const setAbuseHistoryInfo =
    (abuseHistoryInfo: AbuseHistoryModel): AppThunk =>
    async (dispatch) => {
        dispatch(_setAbuseHistoryInfo(abuseHistoryInfo));
    };

export const setIsFetchingAbuseHistory =
    (isFetching: boolean): AppThunk =>
    async (dispatch) => {
        dispatch(_setIsFetchingAbuseHistory(isFetching));
    };

export const setAbuseHistoryOptions =
    (abuseHistoryOptions: AbuseHistoryItem[]): AppThunk =>
    async (dispatch) => {
        dispatch(_setAbuseHistoryOptions(abuseHistoryOptions));
    };

export const saveAbuseHistoryInfo =
    (abuseHistoryInfo: AbuseHistoryModel, _saveLocal = false, gotoNext = false): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        const isUpdate = Boolean(abuseHistoryInfo.Id);
        const url = isUpdate ? `${API_URL}/abusehistory/updateabusehistory/` : `${API_URL}/abusehistory/insertabusehistory/`;

        try {
            const response = await axios.put(url, abuseHistoryInfo);
            const savedAbuseHistory = isUpdate ? abuseHistoryInfo : response.data;
            if (Boolean(response.data)) {
                dispatch(setAbuseHistoryInfo(isUpdate ? abuseHistoryInfo : { ...abuseHistoryInfo, Id: response.data.Id }));
            }
            const isDraftSaved = await dispatch(prepareAbuseHistoryNotes(savedAbuseHistory));
            if (gotoNext && isDraftSaved) {
                dispatch(gotoNextMenu());
            }
            return isDraftSaved;
        } catch (err) {
            dispatch(setAssessmentError({ Show: true, Message: err?.response?.data }));
            return false;
        }
    };

const prepareAbuseHistoryNotes =
    (abuseHistoryInfo: AbuseHistoryModel): AppThunk<Promise<boolean>> =>
    async (dispatch, getState) => {
        if (Boolean(abuseHistoryInfo.Id)) {
            let engagementDraft = copyObject(getState().documentEngagement.engagementDraft);
            let summaryNotes = (engagementDraft.SummaryNotes || []).filter((s) => s.Type !== SummaryNoteTypeEnum.AbuseHistory);
            summaryNotes.push({
                Id: abuseHistoryInfo.MemberId,
                CptCode: '',
                Zcode: '',
                Note: 'Completed Abuse History ',
                Type: SummaryNoteTypeEnum.AbuseHistory,
                IsConfirm: true,
                DisplayName: 'Abuse History',
            });
            const tCodesAndZCodes = [];
            (abuseHistoryInfo.AbuseHistoryList || []).forEach((abuseHistory) => {
                if (abuseHistory.Tcode) {
                    const splitTcode = abuseHistory.Tcode.split('|');
                    const tcode = splitTcode[0];
                    const tcodeDescription = `${tcode} - ${splitTcode[1]}`;
                    const newAbuseHistory = new AbuseHistoryItem();
                    newAbuseHistory.Zcode = tcode;
                    newAbuseHistory.ZcodeDescription = tcodeDescription;
                    tCodesAndZCodes.push(newAbuseHistory);
                }
                const newAbuseHistory = new AbuseHistoryItem();
                newAbuseHistory.Zcode = abuseHistory.Zcode;
                newAbuseHistory.ZcodeDescription = abuseHistory.ZcodeDescription;
                tCodesAndZCodes.push(newAbuseHistory);
            });
            if (abuseHistoryInfo.ViolentCrimeOrTerrorism === YES) {
                const newAbuseHistory = new AbuseHistoryItem();
                newAbuseHistory.Zcode = 'Z65.4';
                newAbuseHistory.ZcodeDescription = 'Victim of crime and terrorism';
                tCodesAndZCodes.push(newAbuseHistory);
            }

            const abuseHistorySummaryNotes = tCodesAndZCodes.map((abuseHistory) => {
                return {
                    Id: abuseHistoryInfo.MemberId,
                    CptCode: 'T1016',
                    Zcode: abuseHistory.Zcode,
                    Note: abuseHistory.TcodeDescription || abuseHistory.ZcodeDescription,
                    Type: SummaryNoteTypeEnum.AbuseHistory,
                    IsConfirm: true,
                    OrderBy: 1,
                    DisplayName: 'Abuse History',
                };
            });

            const updatedSummaryNotes = [...summaryNotes, ...abuseHistorySummaryNotes];

            const summaryNotesWithUniqueZcodes = updatedSummaryNotes.filter(
                (note, index, self) =>
                    index === self.findIndex((n) => n.Type === note.Type && n.Zcode === note.Zcode && n.CptCode === note.CptCode && n.Note === note.Note)
            );
            const summaryNotesWithUniqueTcodes = summaryNotesWithUniqueZcodes.filter(
                (note, index, self) =>
                    index === self.findIndex((n) => n.Type === note.Type && n.Tcode === note.Tcode && n.CptCode === note.CptCode && n.Note === note.Note)
            );

            let memberAssessmentHistory = copyObject(getState().documentEngagement.memberAssessmentHistory || {});
            engagementDraft.SummaryNotes = summaryNotesWithUniqueTcodes;
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                AbuseHistoryStatus: abuseHistoryInfo.AssessmentStatus,
            };
            const updatedEngagementDraft = {
                ...engagementDraft,
                AbuseHistoryId: abuseHistoryInfo.Id,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                EngagementDraft: updatedEngagementDraft,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
            };

            return dispatch(saveEngagementDraft(updatedMemberAssessmentHistory));
        }
        return false;
    };

export const fetchAbuseHistoryInfo =
    (id: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingAbuseHistory(true));
        const response = await axios.get(`${API_URL}/abusehistory/fetchabusehistoryinfobyid?id=${id}`);
        if (response.data) {
            dispatch(setAbuseHistoryInfo(response.data));
        } else {
            dispatch(setAbuseHistoryInfo(new AbuseHistoryModel()));
        }
        dispatch(setIsFetchingAbuseHistory(false));
    };

export const fetchAbuseHistoryByMemberId =
    (memberId: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIsFetchingAbuseHistory(true));
        const response = await axios.get(`${API_URL}/abusehistory/fetchabusehistoryinfobymemberid?memberId=${memberId}`);
        if (response.data) {
            preparePriorAssessmentForNewDraft(response.data);
            dispatch(setAbuseHistoryInfo(response.data));
        } else {
            dispatch(setAbuseHistoryInfo(new AbuseHistoryModel()));
        }
        dispatch(setIsFetchingAbuseHistory(false));
    };

export const fetchAbuseHistoryOptions = (): AppThunk => async (dispatch, getState) => {
    dispatch(setIsFetchingAbuseHistory(true));
    const response = await axios.get(`${API_URL}/abusehistory/fetchabusehistoryoptions`);
    dispatch(setAbuseHistoryOptions(response.data));
    dispatch(setIsFetchingAbuseHistory(false));
};

export default abuseHistorySlice.reducer;
